import {Injectable} from '@angular/core';
import {Actions, createEffect, ofType} from '@ngrx/effects';
import * as CoreActions from './core.actions';
import * as SettingsActions from '../../settings/store/settings.actions';
import * as UserStoreActions from '../../user-access/user-stores-store/user-store.actions';
import {
  catchError,
  distinctUntilChanged,
  exhaustMap,
  filter,
  map,
  mergeMap,
  switchMap,
  toArray,
  withLatestFrom,
} from 'rxjs/operators';
import {from, merge, Observable, of} from 'rxjs';
import {FirebaseService} from '../../../shared-utilities/services-old/firebase.service';
import {CollectionOperationalMessagesServiceService} from '../../../shared/shared-services/firebase/collection-operational-messages.service';
import {path_operational_stores_data_storeId_messages_from_app} from '../../../shared/shared-services/database-paths';
import * as NotificationActions from '../../notifications/store/notification.actions';
import {Action, select, Store} from '@ngrx/store';
import {collection, collectionData, doc, docData, Firestore, query, where} from '@angular/fire/firestore';
import {IStore} from '../../../shared/shared-models/store/store';
import {INotification} from '../../../shared-utilities/models-old/datastructures';
import {IFirebaseDataTimeStamps, IFromAppQueueItem} from '../models';
import {convertIDataTimeStampsToDates} from '../utils/core.effects.utils';
import {resolveProcessingQueueStoreIds} from '../utils/from-app-queue.utils';
import * as UserStoreSelectors from '../../user-access/user-stores-store/user-store.selectors';
import {selectStores} from '../../user-access/user-stores-store/user-store.selectors';
import {createSharedPaths} from '../../../shared/shared-services/database-paths.shared';
import * as UserAccessSelectors from '../../user-access/user-store/user.selectors';
import {IGlobalError} from '../../../shared/shared-components/components/global-error-display/models/global-error';
import {ErrorName} from '../../../shared/shared-components/components/global-error-display/models/global-error.enum';

/**
 * Compares two store lists by store id so timestamp listeners are only rebuilt when the set of
 * stores actually changes, not on every unrelated re-emission of the selector.
 */
function haveSameStoreIds(previous: IStore[], current: IStore[]): boolean {
  if (previous.length !== current.length) {
    return false;
  }
  const previousIds = new Set<string>(previous.map((store: IStore): string => store.storeId));
  return current.every((store: IStore): boolean => previousIds.has(store.storeId));
}

const FROM_APP_QUEUE_READ_CONCURRENCY = 8;

@Injectable()
export class CoreEffects {
  //#region Get | Set User Stores

  // '[Core][User Stores] Get User Stores'
  getUserStores$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserStoreActions.getUserStores),
      withLatestFrom(
        this.store.pipe(select(UserStoreSelectors.selectSelectedStore)),
        this.store.pipe(select(UserAccessSelectors.selectUser)),
      ),
      mergeMap(([action, store, user]) =>
        of(UserStoreActions.getUserStoresSuccess()).pipe(
          catchError((error) =>
            of(
              UserStoreActions.getUserStoresFailure({
                error: {
                  ...error,
                  action: JSON.stringify(action),
                  actionType: action.type,
                  originalError: error,
                  storeIds: [store?.storeId ?? 'unknown'],
                  userId: user?.id ?? 'unknown',
                },
              }),
            ),
          ),
        ),
      ),
    ),
  );
  //'[Core][User Stores] Get User Stores Success'
  getUserStoresSuccess$ = createEffect(() => this.actions$.pipe(
    ofType(UserStoreActions.getUserStoresSuccess),
    map(() => SettingsActions.getAllUserStoreSettings()),
  ));

  //endregion

  // ====================================================================================================
  // Get Image URL
  // ====================================================================================================
  // '[Core][Image] Get Image URL'
  getImageURL = createEffect(() => this.actions$.pipe(
    ofType(CoreActions.getImageURL),
    withLatestFrom(
      this.store.pipe(select(UserStoreSelectors.selectSelectedStore)),
      this.store.pipe(select(UserAccessSelectors.selectUser)),
    ),
    mergeMap(([action, store, user]) =>
      this.firebaseService.getImageUrl(action.path)
        .pipe(
          map((url: string) =>
            CoreActions.getImageURLSuccess({url, image: action.image})),
          catchError((error) => of(
            CoreActions.getImageURLFailure({
              error: {
                ...error,
                action: JSON.stringify(action),
                actionType: action.type,
                originalError: error,
                storeIds: [store.storeId],
                userId: user.id,
              },
              image: action.image,
            })),
          ),
        ),
    ),
  ));

  // ====================================================================================================
  // User Messages
  // ====================================================================================================

  getUserMessages$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CoreActions.getUserMessages),
      withLatestFrom(this.store.pipe(select(selectStores))),
      mergeMap(([_, stores]) => {
        const storeIds = stores.map((store: IStore) => store.storeId);
        if (storeIds.length > 0) {
          const notificationsRef = collection(this.firestore, '/notifications/');
          const q = query(notificationsRef, where('storeId', 'in', storeIds));
          return collectionData(q, {idField: 'id'}).pipe(
            map((notifications: INotification[]) =>
              CoreActions.getUserMessagesSuccess({messages: notifications}),
            ),
            catchError((error) => {
              return of(CoreActions.getUserMessagesFailure({error}));
            }),
          );
        } else {
          return of(CoreActions.getUserMessagesSuccess({messages: []}));
        }
      }),
    ),
  );


  // Every freshness indicator on screen dispatches this action, so the first one wins and the rest are
  // ignored while its listeners are alive. The inner stream follows the user's store list instead of
  // taking a single snapshot of it, so stores that load later - or a different user's stores after a
  // re-login - still get listeners attached rather than staying grey forever.
  loadDataTimeStamps$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CoreActions.getDataTimeStamps),
      exhaustMap((action) =>
        this.store.pipe(
          select(UserStoreSelectors.selectAllUserStores),
          filter((stores: IStore[]) => stores.length > 0),
          distinctUntilChanged(haveSameStoreIds),
          withLatestFrom(this.store.pipe(select(UserAccessSelectors.selectUser))),
          switchMap(([userStores, user]) => {
            return merge(
              ...userStores.map((store: IStore) => {
                const docRef = doc(this.firestore, createSharedPaths().setStoreId(store.storeId).dataTimestamps());
                return docData(docRef)
                  .pipe(
                    map((dataTimeStamps: IFirebaseDataTimeStamps) => {
                      if (dataTimeStamps) {
                        const convertedTimeStamps = convertIDataTimeStampsToDates(dataTimeStamps);
                        return CoreActions.getDataTimeStampsSuccess({
                          store,
                          dataTimeStamps: convertedTimeStamps,
                        });
                      } else {
                        return CoreActions.getDataTimeStampsFailure({
                          error: {
                            action: JSON.stringify(action),
                            actionType: action.type,
                            originalError: 'No data time stamps found',
                            storeIds: [store.storeId],
                            userId: user?.id,
                            message: 'An error occurred whl converting data time stamps.',
                            name: ErrorName.whyGlobalError,
                          },
                        });
                      }
                    }),
                    catchError((error) =>
                      of(CoreActions.getDataTimeStampsFailure({
                        error: {
                          ...error,
                          action: JSON.stringify(action),
                          actionType: action.type,
                          originalError: error,
                          storeIds: [store.storeId],
                          userId: user?.id,
                        },
                      })),
                    ),
                  );
              }),
            );
          }),
        ),
      ),
    ),
  );

  /**
   * Loads every from_app document for the user's stores so the header can show the processing queue.
   * Each store is one one-shot collection read (one read when the queue is empty, one read per waiting message).
   * Store names come from the stores already loaded for the user, so store documents are not read again.
   */
  getFromAppQueue$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CoreActions.getFromAppQueue),
      withLatestFrom(
        this.store.pipe(select(UserStoreSelectors.selectSelectedStore)),
        this.store.pipe(select(UserAccessSelectors.selectUser)),
        this.store.pipe(select(UserStoreSelectors.selectAllUserStores)),
      ),
      switchMap(([action, store, user, allStores]) => {
        const storeIds: string[] = resolveProcessingQueueStoreIds(action.storeIds, allStores, store?.storeId);
        const toFailure = (error: unknown, message: string): Observable<Action> => {
          const globalError = {
            name: ErrorName.whyGlobalError,
            message,
            action: JSON.stringify(action),
            actionType: action.type,
            originalError: error,
            storeIds: storeIds.length > 0 ? storeIds : [store?.storeId ?? 'unknown'],
            userId: user?.id ?? 'unknown',
          } as IGlobalError;
          return of(
            NotificationActions.setGlobalErrorOccurrence({error: globalError}),
            CoreActions.getFromAppQueueFailure({error: globalError}),
          );
        };

        if (storeIds.length === 0) {
          return toFailure(new Error('No stores available'), 'No stores are available for the processing queue.');
        }

        const storeNames = new Map<string, string>(
          (allStores || []).map((userStore: IStore): [string, string] => [userStore.storeId, userStore.name]),
        );
        return from(storeIds).pipe(
          mergeMap((storeId: string) =>
            this.collectionOperationalMessagesService
              .getAllFromAppMessages(path_operational_stores_data_storeId_messages_from_app(storeId))
              .pipe(
                map((items: IFromAppQueueItem[]): IFromAppQueueItem[] =>
                  items.map((item: IFromAppQueueItem): IFromAppQueueItem => ({
                    ...item,
                    storeId,
                    storeName: storeNames.get(storeId) || storeId,
                  })),
                ),
              ),
            FROM_APP_QUEUE_READ_CONCURRENCY,
          ),
          toArray(),
          map((groups: IFromAppQueueItem[][]) =>
            CoreActions.getFromAppQueueSuccess({
              items: groups.flat().sort((left: IFromAppQueueItem, right: IFromAppQueueItem) =>
                (left.storeName || '').localeCompare(right.storeName || '') || left.type.localeCompare(right.type),
              ),
            }),
          ),
          catchError((error: unknown) =>
            toFailure(error, 'Could not load the processing queue.'),
          ),
        );
      }),
    ),
  );

  constructor(
    private actions$: Actions,
    private firebaseService: FirebaseService,
    private firestore: Firestore,
    private store: Store,
    private collectionOperationalMessagesService: CollectionOperationalMessagesServiceService,
  ) {
  }
}
